import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";

import AuthCardFooter from "../../components/auth/AuthCardFooter";
import OtpInput from "../../components/auth/OtpInput";
import styles from "./AuthPage.module.css";

import {
  requestPasswordResetCode,
  verifyResetOtp,
} from "../../services/auth/passwordReset";
import { handleApiError } from "../../utils/handleApiError";

const RESEND_SECONDS = 30;
const OTP_LENGTH = 6;

const VerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  // Guard: if the user lands here without coming from /recover-password,
  // we have no email to tie the code to.
  useEffect(() => {
    if (!email) navigate("/recover-password", { replace: true });
  }, [email, navigate]);

  const [otp, setOtp] = useState("");
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_SECONDS);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const handleResend = async () => {
    if (resendIn > 0) return;
    try {
      // Requesting a new code invalidates any token from a prior verify; we
      // never persist that token (it only lives in nav state), so there is
      // nothing to clear here beyond resetting the input.
      await requestPasswordResetCode(email);
      toast.success("A new verification code has been sent.");
      setOtp("");
      setError(false);
      setErrorMsg("");
      setResendIn(RESEND_SECONDS);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;

    if (otp.length !== OTP_LENGTH) {
      setError(true);
      setErrorMsg(`Enter the ${OTP_LENGTH}-digit code from your email.`);
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    try {
      // Verify the OTP on its own; the backend returns a short-lived reset
      // token that the set-password screen submits with the new password.
      const res = await verifyResetOtp(otp);
      const token = res?.data?.token;
      if (!token) {
        // 200 without a token shouldn't happen, but fail safe rather than
        // forwarding an empty token to the reset screen.
        setError(true);
        setErrorMsg("Invalid or expired code. Please try again.");
        return;
      }
      setError(false);
      setErrorMsg("");
      navigate("/reset-password", { state: { email, token } });
    } catch (err) {
      // 400 { code: "invalid_or_expired_code" }: keep the user here so they
      // can re-enter the code or use Resend.
      if (err?.response?.status === 400) {
        setError(true);
        setErrorMsg("Invalid or expired code. Please try again.");
      } else {
        handleApiError(err);
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className={styles.heading}>Reset your password</h1>
      <p className={styles.subheading}>
        If that email is registered with TOCA, we've sent a 6-digit code to it.
        Enter the code below to continue.
      </p>

      <form onSubmit={handleSubmit} className={styles.form} noValidate>
        <div className={styles.field}>
          <label htmlFor="verify-id" className={styles.label}>
            Email
          </label>
          <div className={styles.inputWrap}>
            <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
            <input
              id="verify-id"
              type="text"
              value={email || ""}
              readOnly
              className={styles.input}
            />
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Enter verification code</label>
          <OtpInput
            length={OTP_LENGTH}
            value={otp}
            onChange={(v) => {
              setOtp(v);
              if (error) {
                setError(false);
                setErrorMsg("");
              }
            }}
            error={error}
            autoFocus
          />
          {error && (
            <span role="alert" className={styles.errorMsg}>
              {errorMsg || `Enter the ${OTP_LENGTH}-digit code from your email.`}
            </span>
          )}
        </div>

        <div>
          <span className={styles.subtleText}>Resend Available: </span>
          {resendIn > 0 ? (
            <span className={styles.subtleText}>
              {String(Math.floor(resendIn / 60)).padStart(2, "0")}:
              {String(resendIn % 60).padStart(2, "0")} min
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              className={styles.inlineLink}
            >
              Resend code
            </button>
          )}
        </div>

        <button type="submit" className={styles.primaryBtn} disabled={loading}>
          {loading ? "Verifying..." : "Verify code"}
        </button>

        <Link to="/login" className={styles.secondaryBtn}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Sign in
        </Link>
      </form>

      <p className={styles.requestAccess}>
        Don't have an account?{" "}
        <a
          href="mailto:sales@tocahealth.com?subject=Access%20request"
          className={styles.requestLink}
        >
          Request access
        </a>
      </p>

      <AuthCardFooter />
    </>
  );
};

export default VerifyOtp;
